import { OrbitControls } from "./orbitControls";
const defaultOptions = {
  hoverStart: () => {},
  hoverEnd: () => {},
  hoverAnimate: () => {},
  invertXAxis: false,
  invertYAxis: false
};
const defaultFov = {
  vertical: 40,
  horizontal: 45
};
export class OrbitControlsWithHover extends OrbitControls {
  constructor(object, domElement, fov, hoverOptions, orbitOptions) {
    super(object, domElement, undefined, orbitOptions);
    Object.defineProperty(this, "_enableHover", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: false
    });
    Object.defineProperty(this, "hasInitialized", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, "fov", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, "captureMouse", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, "isClickDragging", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: false
    });
    Object.defineProperty(this, "hoverStartAzimuth", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: 0
    });
    Object.defineProperty(this, "hoverStartPolar", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, "hoverSettings", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    /**
     * @param event: MouseEvent
     */
    Object.defineProperty(this, "onMouseEnterLocal", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: () => {
        if (this.isClickDragging || !this.hasInitialized) return;
        this.hoverSettings.hoverStart();
        this.hoverStartAzimuth = this.getAzimuthalAngle();
        this.hoverStartPolar = this.getPolarAngle();
        this.captureMouse = true;
      }
    });
    /**
     * @param event: MouseEvent
     */
    Object.defineProperty(this, "onMouseLeaveLocal", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: () => {
        if (this.isClickDragging || !this.hasStartingPosition) return;
        this.captureMouse = false;
        this.hoverSettings.hoverAnimate(this.hoverStartAzimuth || 1, this.hoverStartPolar || 1, OrbitControlsWithHover.RESET_CAMERA_DURATION);
        this.hoverSettings.hoverEnd();
      }
    });
    Object.defineProperty(this, "onMouseMoveLocal", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: e => {
        // eslint-disable-next-line no-bitwise
        if (e.buttons & 1 || e.buttons === undefined && e.which === 1) {
          this.isClickDragging = true;
        } else {
          this.isClickDragging = false;
        }
        if (!this.captureMouse || this.isClickDragging || !this.hasStartingPosition) {
          return;
        }
        const target = e.target;
        const clientWidth = target.offsetWidth;
        const clientHeight = target.offsetHeight;
        const deltaX = e.offsetX - clientWidth / 2;
        const deltaY = e.offsetY - clientHeight / 2;
        const azimuthFov = 45;
        const polarFov = this.fov.vertical;
        let azimuthRelativeDegree = deltaX / clientWidth * azimuthFov;
        let polarRelativeDegree = deltaY / clientHeight * (polarFov || 1);
        if (this.hoverSettings.invertXAxis) {
          azimuthRelativeDegree *= -1;
        }
        if (this.hoverSettings.invertYAxis) {
          polarRelativeDegree *= -1;
        }
        const azimuth = this.hoverStartAzimuth + azimuthRelativeDegree * (Math.PI / 180);
        const polar = this.hoverStartPolar + polarRelativeDegree * (Math.PI / 180);
        this.hoverSettings.hoverAnimate(azimuth, polar, OrbitControlsWithHover.HOVER_CAMERA_DURATION);
      }
    });
    this.fov = Object.assign(Object.assign({}, defaultFov), fov);
    this.hoverSettings = Object.assign(Object.assign({}, defaultOptions), hoverOptions);
    this.captureMouse = true;
    this.hasInitialized = false;
    // Perform initialization of positional information on first update
    const _update = this.update.bind(this);
    this.update = () => {
      const res = _update();
      if (!this.hasInitialized) {
        this.hoverStartAzimuth = this.getAzimuthalAngle();
        this.hoverStartPolar = this.getPolarAngle();
        this.hasInitialized = true;
      }
      return res;
    };
    this.hoverStartAzimuth = this.getAzimuthalAngle();
    this.hoverStartPolar = this.getPolarAngle();
  }
  get hasStartingPosition() {
    return this.hoverStartAzimuth !== undefined && this.hoverStartPolar !== undefined && this.hasInitialized;
  }
  get enableHover() {
    return this._enableHover;
  }
  set enableHover(enabled) {
    this._enableHover = enabled;
    if (enabled) {
      this.domElement.addEventListener("mouseenter", this.onMouseEnterLocal);
      this.domElement.addEventListener("mouseleave", this.onMouseLeaveLocal);
      this.domElement.addEventListener("mousemove",
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      this.onMouseMoveLocal);
    } else {
      this.domElement.removeEventListener("mouseenter", this.onMouseEnterLocal);
      this.domElement.removeEventListener("mouseleave", this.onMouseLeaveLocal);
      this.domElement.removeEventListener("mousemove",
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      this.onMouseMoveLocal);
    }
  }
  dispose() {
    super.dispose();
    this.domElement.removeEventListener("mouseenter", this.onMouseEnterLocal);
    this.domElement.removeEventListener("mouseleave", this.onMouseLeaveLocal);
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    this.domElement.removeEventListener("mousemove", this.onMouseMoveLocal);
  }
}
Object.defineProperty(OrbitControlsWithHover, "RESET_CAMERA_DURATION", {
  enumerable: true,
  configurable: true,
  writable: true,
  value: 0.01
});
Object.defineProperty(OrbitControlsWithHover, "HOVER_CAMERA_DURATION", {
  enumerable: true,
  configurable: true,
  writable: true,
  value: 0.08
});